import * as React from 'react';
import cx from 'classnames';

import Input from 'components/Input';
import Label from 'components/Label';
import Text from 'components/Text';

import './styles/Radio.scss';

/**
 * ## Radio input with [Label](/internal/components/Label).
 * ### Use radio buttons when you would like the user to make exactly one selections out of a limited pool of options (6 or fewer). If you have more options than that, use a [Select](/internal/components/Select).
 *
 *
 * ```example
 * <Field>
 *   <Radio
 *     checked
 *     label="Radio label"
 *   />
 *   <Radio
 *     label="Radio label"
 *     labelNote="Lorem ipsum dolor sit amet, consectetur adipisicing elit. Natus, veniam."
 *   />
 *   <Radio
 *     disabled
 *     label="Radio disabled label"
 *   />
 * </Field>
 * ```
 *
 * ```example
 * <Field>
 *   <Radio
 *     inline
 *     label="Radio label"
 *   />
 *   <Radio
 *     checked
 *     inline
 *     label="Radio label"
 *   />
 *   <Radio
 *     disabled
 *     inline
 *     label="Radio disabled label"
 *   />
 * </Field>
 * ```
 *
 *
 * # TODO
 * * Disabled styling
 */

export type RadioSize = 'small' | 'xsmall' | 'xxsmall';

export enum RadioColor {
  apple = 'apple',
  pencil = 'pencil',
  atlas = 'atlas',
  pool = 'pool',
  mascot = 'mascot',
  pennant = 'pennant',
  chalkboard = 'chalkboard',
  graphite = 'graphite',
  percussion = 'percussion',
  paper = 'paper',
  whiteout = 'whiteout',
  leaf = 'leaf',
  koolaid = 'koolaid',
}

export type Props = {
  /** Native */
  checked?: boolean;
  /** Native */
  disabled?: boolean;
  flush?: boolean;
  /** `inline-block` `display` level */
  inline?: boolean;
  label?: React.ReactNode;
  labelNote?: string;
  // Allows you to add another inline component to the label note. i.e. Link
  customLabelNote?: React.ReactNode;
  /** Native */
  name?: string;
  nonTextLabel?: boolean;
  className?: string;
  size?: RadioSize;
  color?: RadioColor;
  onChange?: (event: React.FormEvent<HTMLInputElement>) => void;
};

class Radio extends React.Component<Props> {
  render() {
    const {
      checked,
      children,
      className,
      customLabelNote,
      flush,
      inline,
      label,
      labelNote,
      nonTextLabel,
      disabled,
      size,
      color,
      ...others
    } = this.props;

    return (
      <Label
        checked={checked}
        note={labelNote}
        customNote={customLabelNote}
        className={cx(
          'Radio',
          {
            'Radio--flush': flush,
            'Radio--inline': inline,
            'Radio--disabled': disabled,
            'Radio--checked': checked,
            'Radio--nonTextLabel': nonTextLabel,
          },
          size && `Radio--${size}`,
          className
        )}>
        <Input
          {...others}
          type="radio"
          checked={checked}
          disabled={disabled}
          className="Radio-input"
        />
        <span className={cx(
          'Radio-indicator',
          color && `Radio-indicator--${color}`,
        )} />
        <Text color={checked ? 'inherit' : 'graphite'} className="Radio-label">
          {label}
          {children}
        </Text>
      </Label>
    );
  }
}

export default Radio;
